"use client";

import { ChevronsUpDown, Ellipsis, Plus } from "lucide-react";
import Link from "next/link";
import { redirect, useSelectedLayoutSegment } from "next/navigation";
import * as React from "react";
import { withSuspense } from "with-suspense";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "~/components/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "~/components/sidebar";
import type { Organization } from "~/lib/auth";
import { getImageUrl } from "~/lib/image";

import { Avatar, AvatarFallback, AvatarImage } from "../avatar";
import { CredenzaTrigger } from "../credenza";
import { Skeleton } from "../skeleton";
import { CreateOrganizationDialog } from "./create-organization-dialog";

export const OrganizationSelect = withSuspense<{ organizationsPromise: Promise<Array<Organization>> }>(({ organizationsPromise }) => {
	const organizations = React.use(organizationsPromise);
	const organizationId = useSelectedLayoutSegment();

	const organization = organizations.find(({ id, slug }) => id === organizationId || slug === organizationId);
	if (!organization) return null;

	return (
		<CreateOrganizationDialog>
			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton
								className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
								size="lg"
							>
								{/* <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
								<activeTeam.logo className="size-4" />
							</div> */}
								<Avatar className="size-8 rounded-lg">
									{organization.logo && <AvatarImage alt={organization.name} src={getImageUrl(organization.logo, { width: 32 })} />}
									<AvatarFallback className="rounded-lg">?</AvatarFallback>
								</Avatar>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-semibold">
										{organization.name}
									</span>
								</div>
								<ChevronsUpDown className="ml-auto" />
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="start"
							className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
							// side={isMobile ? "bottom" : "right"}
							sideOffset={4}
						>
							<DropdownMenuLabel className="text-xs text-muted-foreground">
								Teams
							</DropdownMenuLabel>
							{organizations.slice(0, 10).map(({ id, slug, name, logo }, index) => (
								<DropdownMenuItem
									asChild
									className="gap-2 p-2"
									key={id}
								>
									<Link href={`/${slug}`}>
										<Avatar className="size-6 rounded-sm">
											{logo && <AvatarImage alt={name} src={getImageUrl(logo, { width: 24 })} />}
											<AvatarFallback className="rounded-sm">?</AvatarFallback>
										</Avatar>
										{name}
										<DropdownMenuShortcut>
											⌘
											{index + 1}
										</DropdownMenuShortcut>
									</Link>
								</DropdownMenuItem>
							))}

							<DropdownMenuSeparator />
							{organizations.length >= 10 && (
								<DropdownMenuItem
									asChild
									className="gap-2 p-2 font-medium text-muted-foreground"
								>
									<Link href="/">
										<div className="flex size-6 items-center justify-center rounded-md border bg-background">
											<Ellipsis className="size-4" />
										</div>
										{organizations.length - 10}
										{" "}
										more
									</Link>
								</DropdownMenuItem>
							)}
							<CredenzaTrigger asChild>
								<DropdownMenuItem className="gap-2 p-2">
									<div className="flex size-6 items-center justify-center rounded-md border bg-background">
										<Plus className="size-4" />
									</div>
									<div className="font-medium text-muted-foreground">Add team</div>
								</DropdownMenuItem>
							</CredenzaTrigger>
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
		</CreateOrganizationDialog>
	);
}, {
	fallback: (
		<SidebarMenuButton
			className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
			size="lg"
		>
			<div className="grid flex-1 gap-0.5 text-left text-sm leading-tight">
				<Skeleton className="h-3 w-16 animate-pulse" />
				<Skeleton className="h-3 w-24 animate-pulse" />
			</div>
			<ChevronsUpDown className="ml-auto" />
		</SidebarMenuButton>
	)
});
